import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, TextField, MenuItem } from '@material-ui/core';
import { useQuery } from '@apollo/client';

import type { PhysiciansFilter } from '@/types/Physician';
import { PhysicianCategory, PhysicianCategoryLabels, PhysicianSpecialityColors } from '../enums';
import { list as physiciansList } from '@/graph/physicians';
import SelectInput from '@/se/components/inputs/SelectInput';

interface PhysicianFilterBarProps {
  filter?: PhysiciansFilter;
  value?: PhysiciansFilter;
  onChange: (filter: PhysiciansFilter) => void;
}

const PhysicianFilterBar: React.FC<PhysicianFilterBarProps> = ({ filter, value, onChange }) => {
  const effectiveFilter = (filter ?? value ?? {}) as PhysiciansFilter;

  // Local state for name input to support debounced updates
  const [nameInput, setNameInput] = useState<string>(effectiveFilter.name || '');
  const debounceTimeoutRef = useRef<number | undefined>(undefined);

  // Keep local input in sync when external filter changes (e.g., via URL)
  useEffect(() => {
    setNameInput(effectiveFilter.name || '');
  }, [effectiveFilter.name]);

  // Load existing physician specialities to suggest as autocomplete options
  const { data } = useQuery(physiciansList);

  const specialityOptions = useMemo(() => {
    const fromColors = Object.keys(PhysicianSpecialityColors).filter(key => key !== 'default');
    const fromPhysicians = (data?.physicians ?? [])
      .map((p: any) => p?.speciality as string | undefined)
      .filter(s => !!s && s.trim().length > 0)
      .map(s => s!.trim());

    const set = new Set<string>();
    fromColors.forEach(k => set.add(k));
    fromPhysicians.forEach(s => set.add(s));

    return Array.from(set).sort();
  }, [data]);

  const specialityOptionMap = useMemo(
    () => specialityOptions.reduce((acc, s) => ({ ...acc, [s]: s }), {} as Record<string, string>),
    [specialityOptions]
  );

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setNameInput(value);

    // Clear any pending update
    if (debounceTimeoutRef.current !== undefined) {
      window.clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce updates to avoid spamming the server while typing
    debounceTimeoutRef.current = window.setTimeout(() => {
      const trimmed = value.trim();
      const nextFilter: PhysiciansFilter = { ...effectiveFilter };

      if (trimmed.length >= 2) {
        nextFilter.name = trimmed;
      } else {
        delete nextFilter.name;
      }

      onChange(nextFilter);
    }, 300);
  };

  const handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value || undefined;
    onChange({ ...effectiveFilter, category: newValue });
  };

  const handleSpecialityChange = (newValue: string | null) => {
    const trimmed = newValue?.trim();
    const nextValue = trimmed && trimmed.length > 0 ? trimmed : undefined;
    onChange({ ...effectiveFilter, speciality: nextValue });
  };

  return (
    <Box display="flex" flexWrap="wrap" gridGap={16} mb={1}>
      <TextField label="Name" variant="outlined" size="small" value={nameInput} onChange={handleNameChange} />

      <TextField
        select
        label="Category"
        variant="outlined"
        size="small"
        value={effectiveFilter.category || ''}
        onChange={handleCategoryChange}
        style={{ minWidth: 160 }}
      >
        <MenuItem value="">All</MenuItem>
        {Object.values(PhysicianCategory).map(category => {
          const catValue = category as string;
          return (
            <MenuItem key={catValue} value={catValue}>
              {PhysicianCategoryLabels[catValue] || catValue}
            </MenuItem>
          );
        })}
      </TextField>

      <SelectInput
        name="physician-speciality-filter"
        options={specialityOptionMap}
        value={effectiveFilter.speciality || ''}
        onChange={(val: string) => handleSpecialityChange(val as string | null)}
        placeholder="Speciality"
        isSearchable
        menuWidthFollowContent={false}
        style={{ minWidth: 160 }}
      />
    </Box>
  );
};

export default PhysicianFilterBar;
